import { useState } from "react";
import { toast } from "sonner";
import { MapPin, Phone, EnvelopeSimple, Clock, WhatsappLogo } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { createContact } from "@/lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error("Please fill required fields");
      return;
    }
    setSubmitting(true);
    try {
      await createContact(form);
      toast.success("Message received — we'll reply within a business day.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch {
      toast.error("Could not send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16" data-testid="contact-page">
      <div className="max-w-3xl mb-14">
        <div className="text-xs uppercase tracking-[0.28em] text-brand-sage mb-4">Get in touch</div>
        <h1 className="font-serif text-5xl sm:text-6xl tracking-tight leading-[1.02]">We're just a message away.</h1>
        <p className="mt-6 text-brand-muted text-lg leading-relaxed">
          Questions, feedback, second opinions — we read every message. For emergencies, please call our 24/7 line.
        </p>
      </div>

      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5 space-y-6">
          <div className="bg-white border border-brand-border rounded-3xl p-7">
            <div className="flex gap-4">
              <div className="w-11 h-11 rounded-full bg-[#E8ECE4] flex items-center justify-center shrink-0"><MapPin size={20} className="text-brand-sage" /></div>
              <div>
                <div className="font-serif text-lg">Visit us</div>
                <div className="text-sm text-brand-muted mt-1">Jashoda Multi-Specialty Hospital <br /> Jaluki Stand, Deeg <br />(Rajasthan) 321205</div>
              </div>
            </div>
          </div>
          <div className="bg-white border border-brand-border rounded-3xl p-7">
            <div className="flex gap-4">
              <div className="w-11 h-11 rounded-full bg-[#F0E4DE] flex items-center justify-center shrink-0"><Phone size={20} className="text-[#C85A40]" /></div>
              <div>
                <div className="font-serif text-lg">Call us</div>
                <a href="tel:+919462020447" className="text-sm text-brand-ink hover:text-brand-sage block mt-1" data-testid="contact-phone">+91 9462 02 0447</a>
                <div className="text-xs text-[#C85A40] mt-1 font-medium">Emergency line: 24 / 7</div>
              </div>
            </div>
          </div>
          <div className="bg-white border border-brand-border rounded-3xl p-7">
            <div className="flex gap-4">
              <div className="w-11 h-11 rounded-full bg-[#E4E8EC] flex items-center justify-center shrink-0"><EnvelopeSimple size={20} className="text-brand-sage" /></div>
              <div>
                <div className="font-serif text-lg">Write to us</div>
                <a href="mailto:care@Jashoda Hospital.in" className="text-sm text-brand-ink hover:text-brand-sage block mt-1" data-testid="contact-email">care@jashodahospital.in</a>
              </div>
            </div>
          </div>
          <div className="bg-white border border-brand-border rounded-3xl p-7">
            <div className="flex gap-4">
              <div className="w-11 h-11 rounded-full bg-[#25D366]/20 flex items-center justify-center shrink-0"><WhatsappLogo size={20} className="text-[#128C7E]" weight="fill" /></div>
              <div>
                <div className="font-serif text-lg">WhatsApp</div>
                <a href="https://wa.me/919462020447" target="_blank" rel="noreferrer" className="text-sm text-brand-ink hover:text-brand-sage block mt-1" data-testid="contact-whatsapp">Chat with our team</a>
              </div>
            </div>
          </div>
          <div className="bg-white border border-brand-border rounded-3xl p-7">
            <div className="flex gap-4">
              <div className="w-11 h-11 rounded-full bg-[#EFE9DD] flex items-center justify-center shrink-0"><Clock size={20} className="text-brand-sage" /></div>
              <div>
                <div className="font-serif text-lg">Opening hours</div>
                <div className="text-sm text-brand-muted mt-1">Mon–Sat: 8:00 – 20:00<br />Sun: 9:00 – 14:00</div>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7 space-y-6">
          <div className="rounded-3xl overflow-hidden border border-brand-border bg-white" data-testid="google-map-embed">
            <iframe
              title="Jashoda Hospital map"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1770.1647922227628!2d76.94995276410613!3d27.45899686020348!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3972f100520c101b%3A0x29a30bc36bf30061!2sJashoda%20multispeciality%20hospital!5e0!3m2!1sen!2sin!4v1785066642188!5m2!1sen!2sin"
              width="100%"
              height="360"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>

          <form onSubmit={submit} className="bg-white border border-brand-border rounded-3xl p-8" data-testid="contact-form">
            <h3 className="font-serif text-2xl mb-6">Send a message</h3>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="cname">Name *</Label>
                <Input id="cname" data-testid="contact-name" className="mt-1.5 h-11" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="cemail">Email *</Label>
                <Input id="cemail" type="email" data-testid="contact-email-input" className="mt-1.5 h-11" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="cphone">Phone</Label>
                <Input id="cphone" data-testid="contact-phone-input" className="mt-1.5 h-11" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="csubject">Subject *</Label>
                <Input id="csubject" data-testid="contact-subject" className="mt-1.5 h-11" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
              </div>
            </div>
            <div className="mt-5">
              <Label htmlFor="cmsg">Message *</Label>
              <Textarea id="cmsg" data-testid="contact-message" className="mt-1.5 min-h-[140px]" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit"
              className="mt-6 rounded-full h-12 px-8 bg-brand-sage hover:bg-brand-sageDark text-white transition-transform hover:scale-[1.02]"
            >
              {submitting ? "Sending…" : "Send message"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
